import React, { useState, useEffect, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { get, post } from '@/util/helpers/ajax-helpers';
import { PromiseAwareButton } from '../../components/PromiseAwareButton';
import DatasourceAccess from '../DatasourceAccess';
import { DatasourceAccessPageContainer } from '../../components/DatasourceAccessPageContainer';
import fail from './fail.png';
import wand from './wand.png';
import styles from './DatasourceAccessReview.module.scss';
import { KeySVG } from './icons/keySvg';
import { RefreshSVG } from './icons/RefreshSVG';
import { CheckSVG } from './icons/CheckSVG';
import AjaxSpinner from '../../components/AjaxSpinner';

type Status = 'idle' | 'loading' | 'success' | 'error';

const buttonTexts: Record<Status, string> = {
  idle: 'Approve request',
  loading: 'Granting temporary access',
  success: 'Access granted',
  error: 'Try again',
};

const buttonStyles: Record<Status, string> = {
  idle: styles.requestButton,
  loading: styles.loadingButton,
  success: styles.approvedButton,
  error: styles.tryAgainButton,
};

const icons: Record<Status, ReactElement> = {
  idle: KeySVG,
  loading: <AjaxSpinner className={styles.ajaxLoader} />,
  success: CheckSVG,
  error: RefreshSVG,
};

export const DatasourceAccessReview: React.FC = () => {
  const { datasourceId } = useParams();
  const [status, setStatus] = useState<Status>('idle');
  const [datasourceName, setDatasourceName] = useState('');

  useEffect(() => {
    get(
      'loading the datasource name',
      `/grant/datasource/${datasourceId}/get-name`,
    ).then((json: any) => {
      setDatasourceName(json.name);
    });
  }, []);

  const onSuccess = () => {
    setStatus('success');
  };

  const onError = () => {
    setStatus('error');
  };

  const onLoading = () => {
    setStatus('loading');
  };

  const approve = () => {
    return post('approving the request', `/grant/approve/${datasourceId}`, {
      datasource: datasourceId,
    });
  };

  return (
    <DatasourceAccessPageContainer>
      {status === 'error' && (
        <DatasourceAccess
          image={fail}
          headerText="Access failed"
          descriptionText={
            <span>
              Granting temporary access to the <b>{datasourceName}</b>{' '}
              datasource has failed. Please try again or contact us if the
              problem persists
            </span>
          }
        />
      )}
      {status !== 'error' && (
        <DatasourceAccess
          image={wand}
          headerText="Grant temporary access"
          descriptionText={
            <span>
              Trevor.io Support would like to request 48 hour temporary access
              to the <b>{datasourceName}</b> datasource.
            </span>
          }
        />
      )}

      <PromiseAwareButton
        className={buttonStyles[status]}
        icon={icons[status]}
        onclick={approve}
        onSuccess={onSuccess}
        onError={onError}
        onLoading={onLoading}
        buttonText={buttonTexts[status]}
        disabled={status === 'success'}
      />
    </DatasourceAccessPageContainer>
  );
};

export default DatasourceAccessReview;
